import axios from "axios";
import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { INTEGRADOR, PLATAFORMA } from "../enums/TipoEmpresa";
import { getEmpresaIdLogada } from "../utils/getEmpresaLogada";
import { obterStorage, removerStorage, salvarStorage } from "../utils/secureStorage";
import { baseURL, useApi, versaoClienteDTO } from "./api";

export const AuthContext = createContext({});

const TIPOS_EMPRESA_ORIGEM_COM_PERFIL_MASTER = [PLATAFORMA, INTEGRADOR];
const TIPO_PERFIL_USUARIO_MASTER = "MASTER";

function decodificarTokenJwt(token) {
  if (!token) {
    return null;
  }

  try {
    const [, payload] = token.split(".");
    if (!payload) {
      return null;
    }

    const base64 = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payload.length / 4) * 4, "=");
    const binario = atob(base64);
    const bytes = Uint8Array.from(binario, (caractere) => caractere.charCodeAt(0));

    return JSON.parse(new TextDecoder().decode(bytes));
  } catch (error) {
    console.error("Não foi possível decodificar o token de acesso.", error);
    return null;
  }
}

function normalizarTipoEmpresa(tipoEmpresa) {
  const tipoRecebido =
    typeof tipoEmpresa === "string"
      ? tipoEmpresa
      : (tipoEmpresa?.tipoEmpresa ?? tipoEmpresa?.tipo ?? null);

  return typeof tipoRecebido === "string" ? tipoRecebido.trim().toUpperCase() : null;
}

async function aplicarPerfilMasterPorEmpresaOrigem(dadosSessao, idEmpresaSolicitada) {
  const token = dadosSessao?.tokenDTO?.token;
  const dadosToken = decodificarTokenJwt(token);
  const idEmpresaOrigem = dadosToken?.idEmpresaOrigem;
  const idEmpresaToken = dadosToken?.idEmpresa;
  const idEmpresaAcessada = dadosSessao?.empresaDTO?.id;
  const perfilUsuario = dadosSessao?.usuarioLogadoDTO?.usuarioPerfilEmpresaDTO;
  const expiracaoToken = Number(dadosToken?.exp);

  if (
    !token ||
    !Number.isFinite(expiracaoToken) ||
    Date.now() >= expiracaoToken * 1000 ||
    idEmpresaOrigem == null ||
    idEmpresaToken == null ||
    idEmpresaAcessada == null ||
    idEmpresaSolicitada == null ||
    String(idEmpresaOrigem) === String(idEmpresaAcessada) ||
    String(idEmpresaToken) !== String(idEmpresaAcessada) ||
    String(idEmpresaAcessada) !== String(idEmpresaSolicitada) ||
    !perfilUsuario
  ) {
    return dadosSessao;
  }

  try {
    const api = axios.create({
      baseURL: baseURL,
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        Authorization: `Bearer ${token}`,
        "X-IdEmpresa": idEmpresaAcessada,
      },
    });

    const response = await api.get(`/configuracoes-lojas/empresa/tipo-empresa/${idEmpresaOrigem}`);
    const tipoEmpresaOrigem = normalizarTipoEmpresa(response?.data);

    if (!TIPOS_EMPRESA_ORIGEM_COM_PERFIL_MASTER.includes(tipoEmpresaOrigem)) {
      return dadosSessao;
    }

    return {
      ...dadosSessao,
      usuarioLogadoDTO: {
        ...dadosSessao.usuarioLogadoDTO,
        usuarioPerfilEmpresaDTO: {
          ...perfilUsuario,
          tipoPerfilUsuario: TIPO_PERFIL_USUARIO_MASTER,
        },
      },
    };
  } catch (error) {
    console.error("Não foi possível validar a empresa de origem.", error);
    return dadosSessao;
  }
}

function parseDataBrasileira(dataStr) {
  if (!dataStr || typeof dataStr !== "string") return null;
  const partes = dataStr.split("-");
  if (partes.length !== 3) return null;
  return new Date(parseInt(partes[2]), parseInt(partes[1]) - 1, parseInt(partes[0]));
}

export const AuthProvider = ({ children }) => {
  const { definirToken, token } = useApi();
  const [dadosAuth, setDadosAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logado, setLogado] = useState(false);
  const [pagamentoNecessario, setPagamentoNecessario] = useState(false);
  const [usuarioMaster, setUsuarioMaster] = useState(false);
  const [homologacao, setHomologacao] = useState();
  const [contratoWaymenuAtivo, setContratoWaymenuAtivo] = useState(false);
  const [contratoWaymenuAtualDTO, setContratoWaymenuAtualDTO] = useState(null);
  const [contratoWaychefAtivo, setContratoWaychefAtivo] = useState(false);
  const [limiteMaximoDiasRelatorio, setLimiteMaximoDiasRelatorio] = useState(null);

  useEffect(() => {
    retomarSessao();
  }, []);

  useEffect(() => {
    if (dadosAuth && dadosAuth.tipoLogin === "PAGAMENTO_NECESSARIO") {
      setPagamentoNecessario(true);
      let mensagem = "Seu plano expirou! Contate um supervisor para realizar a renovação.";
      if (dadosAuth.usuarioLogadoDTO.usuarioPerfilEmpresaDTO.tipoPerfilUsuario === "MASTER") {
        mensagem = "Seu plano expirou! Renove agora para continuar.";
      }
      toast.error(mensagem, {
        position: "top-right",
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
        closeButton: true,
      });
    } else {
      setPagamentoNecessario(false);
    }
  }, [dadosAuth]);

  useEffect(() => {
    try {
      if (
        dadosAuth &&
        dadosAuth.usuarioLogadoDTO.usuarioPerfilEmpresaDTO.tipoPerfilUsuario === "MASTER"
      ) {
        setUsuarioMaster(true);
      } else {
        setUsuarioMaster(false);
      }
    } catch (error) {
      setUsuarioMaster(false);
    }
  }, [dadosAuth]);

  useEffect(() => {
    if (dadosAuth?.tokenDTO?.token) {
      checarHomologacao();
      checarEmpresaContrato();
    }
  }, [dadosAuth?.tokenDTO?.token, dadosAuth?.empresaDTO?.id]);

  function checarHomologacao() {
    if (dadosAuth) {
      const idEmpresa = getEmpresaIdLogada();
      const api = axios.create({
        baseURL: baseURL,
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${dadosAuth.tokenDTO.token}`,
          ...(idEmpresa && { "X-IdEmpresa": idEmpresa }),
        },
      });

      api
        .get(`/waybe/configuracao/1`)
        .then((response) => {
          const limite = response.data?.limiteMaximoDiasRelatorio ?? 730;
          const isHomologacao = !!response.data?.homologacao;

          setHomologacao(isHomologacao);
          setLimiteMaximoDiasRelatorio(limite);

          const dadosSessao = obterStorage("_da") || dadosAuth;

          if (dadosSessao && dadosSessao.limiteMaximoDiasRelatorio !== limite) {
            const sessaoAtualizada = {
              ...dadosSessao,
              limiteMaximoDiasRelatorio: limite,
            };

            salvarStorage("_da", sessaoAtualizada);
            salvarStorage("sessao", sessaoAtualizada);
            setDadosAuth(sessaoAtualizada);
          }
        })
        .catch((error) => {
          toast.error("Erro ao obter configurações Waybe.");
          console.error(error);
        });
    }
  }

  function checarEmpresaContrato() {
    if (dadosAuth) {
      const api = axios.create({
        baseURL: baseURL,
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${dadosAuth.tokenDTO.token}`,
        },
      });

      api
        .get(`/contrato/empresa-contrato-waybe/planos-ativos`)
        .then((response) => {
          setContratoWaymenuAtivo(response?.data?.contratoWaymenu ? true : false);
          setContratoWaychefAtivo(response?.data?.contratoWaychef ? true : false);
        })
        .catch((error) => {
          toast.error("Erro ao obter configurações do contrato sobre planos ativos.");
          console.error(error);
        });
    }
  }

  function retomarSessao() {
    setLoading(true);

    const urlParams = new URLSearchParams(window.location.search);
    const tokenURL = urlParams.get("sessao");

    if (tokenURL) {
      setDadosAuth(null);
      definirToken(tokenURL);
      setLogado(true);
      setLoading(false);
      return;
    }

    const object = obterStorage("_da");
    if (object) {
      setDadosAuth(object);
      definirToken(object.tokenDTO.token);
      setContratoWaymenuAtualDTO(object?.empresaDTO?.contratoWaymenuAtualDTO || null);
      setLogado(true);
      setLoading(false);
      verificarNotificacaoContrato(object);
    } else {
      setDadosAuth(null);
      setLogado(false);
      setLoading(false);
    }
  }

  function realizarLogin(email, senha, forcarLogin = false) {
    return new Promise((resolve, reject) => {
      const api = axios.create({
        baseURL: baseURL,
      });

      api
        .post("/usuario-sessao/client/usuario/erp", {
          username: email,
          password: senha,
          forcarLogin: forcarLogin,
          clientKey: "5202BFAFF162A71345CC0F3DDE1940A4",
          tipoClient: "ERP",
          versaoClientDTO: versaoClienteDTO,
        })
        .then(async (dadosLogin) => {
          setDadosAuth(dadosLogin.data);

          salvarStorage("_da", dadosLogin.data);
          salvarStorage("sessao", dadosLogin.data);
          definirToken(dadosLogin.data.tokenDTO.token);
          document.cookie = "notificacaoLogadoExibir=false";
          sessionStorage.setItem("_showContractNotif", "true");
          resolve(dadosLogin.data.tokenDTO.token);
        })
        .catch((error) => {
          setLogado(false);
          reject(error);
        });
    });
  }

  function realizarLogoff() {
    removerStorage("_da");
    removerStorage("lista_dashboards");
    removerStorage("sessao");
    document.cookie = "notificacaoLogadoExibir=false";
    setDadosAuth(null);
    setLogado(false);
  }

  function trocarEmpresa(empresa) {
    return new Promise((resolve, reject) => {
      const api = axios.create({
        baseURL: baseURL,
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${dadosAuth.tokenDTO.token}`,
        },
      });

      api
        .post(`/usuario-sessao/client/usuario/erp/trocar-empresa/${empresa.idEmpresa}`)
        .then(async (dadosLogin) => {
          // dadosAuth, storage e token só mudam juntos, abaixo: atualizar dadosAuth antes do await
          // expõe a empresa nova com o token antigo (já invalidado) e gera 401 nas telas que reagem a ela.
          const dadosSessao = await aplicarPerfilMasterPorEmpresaOrigem(
            dadosLogin.data,
            empresa.idEmpresa,
          );

          setDadosAuth(dadosSessao);
          salvarStorage("_da", dadosSessao);
          salvarStorage("sessao", dadosSessao);
          definirToken(dadosSessao.tokenDTO.token);
          sessionStorage.setItem("_showContractNotif", "true");
          resolve(dadosSessao);
        })
        .catch((error) => {
          if (error?.response?.status === 404) {
            toast.error("Não existe recorrência de mensalidade configurada");
            setLoading(false);
            setLogado(false);
            reject(error);
            return;
          }
          reject(error);
        });
    });
  }

  function getDadosEmpresa() {
    return new Promise((resolve, reject) => {
      const api = axios.create({
        baseURL: baseURL,
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${token}`,
        },
      });

      api
        .get(`configuracoes-lojas/empresa/` + dadosAuth.empresaDTO.id)
        .then((dadosEmpresa) => {
          resolve(dadosEmpresa.data);
        })
        .catch((error) => {
          toast.error("Erro ao obter dados da empresa");
          reject(error);
        });
    });
  }

  function verificarNotificacaoContrato(dados) {
    if (!sessionStorage.getItem("_showContractNotif")) {
      return;
    }
    sessionStorage.removeItem("_showContractNotif");

    const empresa = dados?.empresaDTO;
    if (!empresa) return;

    const { diasParaVencimento, prorrogacao } = empresa;

    if (
      diasParaVencimento !== undefined &&
      diasParaVencimento !== null &&
      diasParaVencimento <= 10 &&
      diasParaVencimento >= 0
    ) {
      toast.warning(`Sua licença expira em ${diasParaVencimento} dia(s).`, {
        autoClose: 8000,
      });
      return;
    }

    if (diasParaVencimento !== undefined && diasParaVencimento < 0 && prorrogacao != null) {
      const hoje = new Date();
      const currentDate = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
      const prorrogacaoDate = parseDataBrasileira(prorrogacao);

      if (prorrogacaoDate && currentDate <= prorrogacaoDate) {
        toast.warning(
          `Sua licença foi prorrogada até ${prorrogacao.replace(/-/g, "/")}. Renove para continuar utilizando o sistema.`,
          { autoClose: 8000 },
        );
      } else {
        console.error("Não exibiu toast: data de prorrogação expirada ou inválida.");
      }
    } else {
      console.error("Não entrou na Regra 2");
    }
  }

  return (
    <AuthContext.Provider
      value={{
        dadosAuth,
        loading,
        logado,
        pagamentoNecessario,
        setLoading,
        setLogado,
        setPagamentoNecessario,
        realizarLogin,
        trocarEmpresa,
        usuarioMaster,
        homologacao,
        getDadosEmpresa,
        contratoWaymenuAtivo,
        contratoWaychefAtivo,
        contratoWaymenuAtualDTO,
        limiteMaximoDiasRelatorio,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
